import React, { useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { WfButton } from "@/components/site/primitives";
import { PHOTO_CREDIT } from "@/content/worldFarmers";
import { scrollToId } from "@/lib/scrollTo";

const CHAPTERS = [
  {
    year: "1984",
    kicker: "Where it began",
    line: "It started with a few families and a patch of river-bottom land in Lancaster.",
    src: "/images/wf/farm-3754.jpg",
    alt: "A black-and-white archival photograph of a worker harvesting vegetables among dense foliage.",
    bw: true,
  },
  {
    year: "2005",
    kicker: "Taking root",
    line: "The farmers organized — and the Flats Mentor Farm was born.",
    src: "/images/wf/farm-91.jpg",
    alt: "A farmer sitting on a small stool while working in a field with a wooden trellis behind.",
  },
  {
    year: "Today",
    kicker: "A thriving community",
    line: "1,200 farmers. 55 acres. Culturally relevant food feeding all of New England.",
    src: "/images/wf/farm-fabiola.jpg",
    alt: "A woman in colorful traditional clothing harvesting greens in an open field under a dramatic sky.",
  },
];

const RANGES = [
  [0, 0.26, 0.34],
  [0.32, 0.44, 0.6, 0.68],
  [0.66, 0.8, 1],
];
const OUT = [
  [1, 1, 0],
  [0, 1, 1, 0],
  [0, 1, 1],
];

export const HeroV3 = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(v < 0.34 ? 0 : v < 0.68 ? 1 : 2);
  });

  const op0 = useTransform(scrollYProgress, RANGES[0], OUT[0]);
  const op1 = useTransform(scrollYProgress, RANGES[1], OUT[1]);
  const op2 = useTransform(scrollYProgress, RANGES[2], OUT[2]);
  const ops = [op0, op1, op2];
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.28]);
  const cueOp = useTransform(scrollYProgress, [0, 0.1], [1, 0]);

  const go = (id) => (e) => { e.preventDefault(); scrollToId(id); };

  return (
    <section id="top" ref={ref} className="relative h-[320vh] bg-[var(--wf-soil)]">
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Chapter images */}
        {CHAPTERS.map((c, i) => (
          <motion.img
            key={c.src}
            src={c.src}
            alt={i === active ? c.alt : ""}
            aria-hidden={i === active ? undefined : "true"}
            loading={i === 0 ? "eager" : "lazy"}
            style={reduce ? { opacity: i === 0 ? 1 : 0 } : { opacity: ops[i], scale }}
            className={`absolute inset-0 h-full w-full object-cover ${c.bw ? "grayscale" : ""}`}
          />
        ))}

        {/* Scrim */}
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/35 to-black/20" />
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

        {/* Progress rail (desktop) */}
        <div aria-hidden="true" className="absolute right-8 top-1/2 hidden -translate-y-1/2 lg:block">
          <div className="relative h-56 w-px bg-white/25">
            <motion.div
              className="absolute left-0 top-0 w-px origin-top bg-[var(--wf-clay)]"
              style={reduce ? { height: "100%" } : { height: "100%", scaleY: scrollYProgress }}
            />
            {CHAPTERS.map((c, i) => (
              <div key={c.year} className="absolute right-[-5px] flex flex-row-reverse items-center gap-3" style={{ top: `${(i / (CHAPTERS.length - 1)) * 100}%` }}>
                <span className={`h-2.5 w-2.5 rounded-full transition-colors ${active >= i ? "bg-[var(--wf-clay)]" : "bg-white/40"}`} />
                <span className={`whitespace-nowrap text-xs font-semibold tracking-wide transition-colors ${active === i ? "text-white" : "text-white/50"}`}>{c.year}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Chapter text (cross-fade) */}
        <div className="relative z-10 mx-auto flex h-full max-w-6xl items-center px-6 sm:px-8">
          <div className="relative w-full max-w-xl">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.year}
                style={reduce ? { opacity: i === 0 ? 1 : 0, position: i === 0 ? "relative" : "absolute" } : { opacity: ops[i], position: i === 0 ? "relative" : "absolute" }}
                className="inset-0 top-0"
                aria-hidden={i === active ? undefined : "true"}
              >
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-[var(--wf-clay)]">
                  <span aria-hidden="true" className="h-px w-8 bg-[var(--wf-clay)]/70" />
                  {c.kicker}
                </span>
                <div className="mt-3 font-serif text-[4rem] font-semibold leading-none text-white sm:text-[6rem]">{c.year}</div>
                <p className="mt-5 max-w-md text-lg leading-8 text-[rgba(255,253,248,0.92)] [text-shadow:0_1px_16px_rgba(0,0,0,0.5)]">{c.line}</p>
              </motion.div>
            ))}

            {/* Persistent H1 for a11y + CTAs */}
            <h1 data-testid="hero-headline" className="sr-only">World Farmers — four decades of growing farmers and communities since 1984.</h1>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <WfButton as="a" href="#get-involved" onClick={go("get-involved")} withArrow variant="light" data-testid="hero-primary-cta-button">
                Get Involved
              </WfButton>
              <WfButton
                as="a"
                href="#where-to-buy"
                onClick={go("where-to-buy")}
                data-testid="hero-secondary-cta-button"
                className="border border-white/60 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20"
              >
                Where to buy crops
              </WfButton>
            </div>
          </div>
        </div>

        {/* Credit + cue */}
        <a href={PHOTO_CREDIT.href} target="_blank" rel="noopener noreferrer" className="absolute bottom-4 right-4 z-10 rounded-full bg-[rgba(0,0,0,0.45)] px-3 py-1 text-[11px] font-medium text-white backdrop-blur-sm hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
          {PHOTO_CREDIT.label}
        </a>
        <motion.div style={reduce ? undefined : { opacity: cueOp }} className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-center text-white">
          <span className="block text-[11px] uppercase tracking-[0.2em] text-white/80">Scroll the story</span>
          <ChevronDown className="mx-auto mt-1 h-5 w-5 motion-safe:animate-bounce" aria-hidden="true" />
        </motion.div>
      </div>
    </section>
  );
};
